import { API_BASE_URL } from "../config/apiConfig";
import { redirectToLoginWithError, redirectToForbiddenWithError } from "./authRedirect";
import {
  HINT_401,
  HINT_403,
  HINT_502,
  HINT_5XX,
  SESSIE_VERLOPEN,
  GEEN_TOEGANG_PAGINA,
} from "./authMessages";

// Helpers
// Wachten dat vroeg stopt als de caller afbreekt (een Retry-After kan tot 30 s duren).
function sleep(ms, signal) {
  return new Promise((r) => {
    const t = setTimeout(r, ms);
    signal?.addEventListener?.(
      "abort",
      () => {
        clearTimeout(t);
        r();
      },
      { once: true }
    );
  });
}
function withJitter(base, attempt) {
  const exp = base * Math.pow(2, attempt);
  const jitter = 0.5 + Math.random(); // 0.5x - 1.5x
  return Math.min(8000, Math.round(exp * jitter));
}

// Bij een 429 met Retry-After wacht de retry-lus zo lang als de server vraagt, begrensd.
export const MAX_RETRY_AFTER_MS = 30000;

// Parse minimal WWW-Authenticate challenges (claims/resource)
function parseWwwAuthenticate(headerValue) {
  if (!headerValue || typeof headerValue !== "string") return {};
  const lower = headerValue.toLowerCase();
  if (!lower.startsWith("bearer")) return {};
  const obj = {};
  const parts = headerValue.substring(6).split(/,\s*/);
  for (const p of parts) {
    const m = p.match(/([a-zA-Z_]+)=(?:"([^"]*)"|([^,\s]+))/);
    if (m) {
      const k = m[1];
      const v = m[2] ?? m[3] ?? "";
      obj[k] = v;
    }
  }
  return obj; // e.g., { error: "insufficient_claims", claims: "{...}", resource: "api://..." }
}

export class ApiError extends Error {
  constructor(
    message,
    {
      status,
      code,
      correlationId,
      responseBody,
      isTimeout = false,
      isNetwork = false,
      isAborted = false,
      retryAfterSeconds = null,
    } = {}
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    // Stabiele envelope-foutcode (bv. "forbidden_role", "token_invalid"), zodat
    // callers niet opnieuw de body hoeven te parsen om een fout te classificeren.
    this.code = code ?? null;
    this.correlationId = correlationId;
    this.responseBody = responseBody;
    this.isTimeout = isTimeout;
    this.isNetwork = isNetwork;
    // Bewust afgebroken door de caller (AbortSignal), geen fout van server of netwerk.
    this.isAborted = isAborted;
    // Bij 429 (code rate_limited): de Retry-After-header in seconden, of null als die ontbrak.
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// Retry-After in seconden (enkel de delta-seconds-vorm; een HTTP-datum levert null op).
export function parseRetryAfter(headerValue) {
  if (headerValue == null || headerValue === "") return null;
  const n = Number(String(headerValue).trim());
  return Number.isFinite(n) && n >= 0 ? Math.ceil(n) : null;
}

function buildUrl(path, query) {
  const base = path.startsWith("/") ? path.slice(1) : path;
  let qs = "";
  if (query) {
    // Skip undefined/null/empty values: URLSearchParams would otherwise stringify
    // `undefined` to the literal "undefined", which callers pass to mean "no filter".
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      params.append(k, v);
    }
    qs = params.toString();
  }
  return `${API_BASE_URL}/${base}${qs ? `?${qs}` : ""}`;
}

// Detect environment for header strategy without env vars
const isProd =
  typeof window !== "undefined" &&
  window.location &&
  !(
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "0.0.0.0"
  );
const isSWAHost =
  typeof window !== "undefined" &&
  window.location &&
  (window.location.hostname.endsWith(".azurestaticapps.net") ||
    window.location.hostname.endsWith(".azurestaticapps.dev") ||
    window.location.hostname === "www.manuco.be");
// note: both X-Auth-Token and Authorization headers are sent in prod/SWA for compatibility

// Reusable auth header builder (exported for streaming / special cases)
export function buildAuthHeaders(token) {
  const headers = {};
  if (!token) return headers;
  if (isProd || isSWAHost) {
    headers["X-Auth-Token"] = token;
    headers["Authorization"] = `Bearer ${token}`;
  } else {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// Decode JWT segment (header/payload) Base64URL -> JSON
function decodeJwtPart(part) {
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function decodeJwtPayload(token) {
  try {
    const [, payload] = token.split(".");
    return decodeJwtPart(payload);
  } catch {
    return null;
  }
}

function decodeJwtHeader(token) {
  try {
    const [header] = token.split(".");
    return decodeJwtPart(header);
  } catch {
    return null;
  }
}

// validation_failed: de veldmeldingen uit error.details ({ veld: [meldingen] }) compact achter de
// melding, zodat features dat niet elk zelf moeten doen. Enkel tekstmeldingen; technische
// details-velden (reason, aantallen, objecten) blijven weg. Maximaal 5 velden.
const MAX_DETAIL_FIELDS = 5;
export function validationDetailsSuffix(body) {
  const err = body && typeof body === "object" ? body.error : null;
  if (!err || typeof err !== "object") return "";
  if ((err.code || err.Code) !== "validation_failed") return "";
  const details = err.details ?? err.Details;
  if (!details || typeof details !== "object" || Array.isArray(details)) return "";
  const parts = [];
  for (const [field, msgs] of Object.entries(details)) {
    if (field === "reason") continue;
    const list = (Array.isArray(msgs) ? msgs : [msgs]).filter(
      (m) => typeof m === "string" && m.trim() !== ""
    );
    if (!list.length) continue;
    const text = list.join(", ");
    parts.push(field && field !== "body" ? `${field}: ${text}` : text);
  }
  if (!parts.length) return "";
  const shown = parts.slice(0, MAX_DETAIL_FIELDS).join("; ");
  const more = parts.length > MAX_DETAIL_FIELDS ? `; +${parts.length - MAX_DETAIL_FIELDS} meer` : "";
  return ` (${shown}${more})`;
}

export function formatErrorMessage(status, statusText, corr, body) {
  // Prefer exact SQL rawMessage if the backend provided it
  if (typeof body === "object" && body && body.rawMessage) {
    return `${body.rawMessage}`;
  }

  const base = `${status} ${statusText || ""}`.trim();
  let hint = "";
  if (status === 401) hint = HINT_401;
  else if (status === 403) hint = HINT_403;
  else if (status === 502) hint = HINT_502;
  else if (status >= 500) hint = HINT_5XX;
  const corrSuffix = corr ? ` corr ${corr}` : "";

  const isObj = typeof body === "object" && body !== null;
  // Prefer backend envelope error message when available
  let apiMsgRaw = "";
  if (isObj && ("success" in body) && body.success === false && body.error) {
    if (typeof body.error === "string") apiMsgRaw = body.error;
    else if (typeof body.error?.Message === "string") apiMsgRaw = body.error.Message;
    else if (typeof body.error?.message === "string") apiMsgRaw = body.error.message;
  }
  // Fallbacks: plain top-level message/error or string body
  if (!apiMsgRaw) {
    if (isObj) {
      if (typeof body.message === "string") apiMsgRaw = body.message;
      else if (typeof body.error === "string") apiMsgRaw = body.error;
    } else if (typeof body === "string") {
      apiMsgRaw = body;
    }
  }
  const apiMsg = apiMsgRaw ? `: ${apiMsgRaw}${validationDetailsSuffix(body)}` : "";

  // Extra context for common SQL date conversion failures
  let context = "";
  if (apiMsgRaw && /Conversion failed when converting date/i.test(apiMsgRaw)) {
    context = " — When inserting records into the database.";
  }

  // Include SQL procedure/line when provided by backend
  let procInfo = "";
  if (isObj) {
    const proc = body.procedure || (Array.isArray(body.details) && body.details[0]?.procedure);
    const line = body.line || (Array.isArray(body.details) && body.details[0]?.line);
    if (proc) {
      procInfo = ` — Procedure: ${proc}${line ? ` (line ${line})` : ""}`;
    }
  }

  return `${base}${corrSuffix}${apiMsg}${hint ? ` — ${hint}` : ""}${context}${procInfo}`;
}

function shouldRetry({ method, status, isNetworkError, retryOnStatus, retryOnMethods }) {
  const m = (method || "GET").toUpperCase();
  const methodAllowed = retryOnMethods.includes(m);
  if (!methodAllowed) return false;
  if (isNetworkError) return true;
  return status != null && retryOnStatus.includes(status);
}

export async function apiRequest(
  path,
  {
    method = "GET",
    query,
    body,
    tokenProvider,
    timeoutMs,
    retries = 2,
    retryDelayBaseMs = 300,
    retryOnStatus = [429, 502, 503, 504],
    retryOnMethods = ["GET", "HEAD"],
    suppressAuthRedirect = false,
    // Optioneel AbortSignal van de caller: afbreken stopt de request én alle retries.
    signal,
  } = {}
) {
  const abortedError = () => new ApiError("Request geannuleerd", { isAborted: true });
  if (signal?.aborted) throw abortedError();
  const url = buildUrl(path, query);
  const headers = { Accept: "application/json" };

  // Exact import can legitimately take >15s (Exact + DB work); bump floor to 120s for that route.
  const isExactImport = typeof path === "string" && path.startsWith("exact/import");
  // Leesvragen krijgen standaard 30 s: na een timeout volgt geen herhaling meer (#79), dus één
  // langere poging i.p.v. tot 3 × 15 s die de database dubbel belasten.
  const upperMethod = (method || "GET").toUpperCase();
  const isRead = upperMethod === "GET" || upperMethod === "HEAD";
  const baseTimeout = timeoutMs ?? (isRead ? 30000 : 15000);
  const effectiveTimeout = isExactImport && baseTimeout < 120000 ? 120000 : baseTimeout;

  let token = null;
  if (typeof tokenProvider === "function") {
    try {
      token = await tokenProvider();
  } catch {}
  }
  if (token) {
    if (isProd || isSWAHost) {
      // In production/SWA: send both headers for compatibility
      headers["X-Auth-Token"] = token;
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      // Local: standard Bearer
      headers["Authorization"] = `Bearer ${token}`;
    }
    // Debug logging: only in dev builds and when DEBUG_TOKEN === 'debug' to avoid accidental noise
    if (
      import.meta.env.DEV &&
      typeof window !== "undefined" &&
      window.localStorage?.getItem("DEBUG_TOKEN") === "debug"
    ) {
      const claims = decodeJwtPayload(token);
      const header = decodeJwtHeader(token);
      if (claims) {
        const exp = claims.exp ? new Date(claims.exp * 1000).toISOString() : undefined;
        console.debug("Token claims", {
          aud: claims.aud,
          iss: claims.iss,
          roles: claims.roles,
          scp: claims.scp,
          expIso: exp,
        });
      }
      if (header) {
        console.debug("Token header", { alg: header.alg, kid: header.kid });
      }
      console.debug(
        "Auth headers used",
        isProd || isSWAHost ? "X-Auth-Token + Authorization" : "Authorization"
      );
    }
  }
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let lastError;
  let attemptedAuthRetry = false;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (signal?.aborted) throw abortedError();
    const controller = new AbortController();
    const to = setTimeout(() => controller.abort("timeout"), effectiveTimeout);
    const onExternalAbort = () => controller.abort("cancelled");
    signal?.addEventListener?.("abort", onExternalAbort, { once: true });

    try {
      const res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      clearTimeout(to);
      signal?.removeEventListener?.("abort", onExternalAbort);

      const corr =
        res.headers.get("X-Correlation-ID") || res.headers.get("x-correlation-id") || null;
      const wwwAuth =
        res.headers.get("WWW-Authenticate") || res.headers.get("www-authenticate") || "";
      const ct = res.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      let payload;
      try {
        payload = isJson ? await res.json() : await res.text();
  } catch {
        payload = undefined;
      }

      if (!res.ok) {
        const message = formatErrorMessage(res.status, res.statusText, corr, payload);
        // Stabiele foutcode uit de envelope; consumers classificeren hierop
        // (autorisatie-oordeel vs. technische fout) zonder de body te parsen.
        const envError = payload && typeof payload === "object" ? payload.error : null;
        const errorCode = envError?.code || envError?.Code || null;
        const retryAfterSeconds = parseRetryAfter(res.headers.get("Retry-After"));
        if (res.status === 401 || res.status === 403) {
          // Exact-specifieke 401's (verlopen Exact-koppeling) zijn geen webapp-sessieprobleem:
          // altijd doorlaten naar de feature-laag (ExactErrorNotice), nooit modal/redirect.
          // v1: code "exact_login_required"; v2: code "unauthenticated" + details.reason.
          const isExactAuthError =
            errorCode === "exact_login_required" ||
            envError?.details?.reason === "exact_login_required";
          // Try a one-time silent auth retry before redirecting
          if (!attemptedAuthRetry && !isExactAuthError && typeof tokenProvider === "function") {
            attemptedAuthRetry = true;
            try {
              const fresh = await tokenProvider();
              if (fresh) {
                if (isProd || isSWAHost) {
                  headers["X-Auth-Token"] = fresh;
                  headers["Authorization"] = `Bearer ${fresh}`;
                } else {
                  headers["Authorization"] = `Bearer ${fresh}`;
                }
                // retry immediately without counting against retries
                attempt--; // counteract the for-loop increment
                continue;
              }
            } catch {
              /* ignore and fall through to redirect */
            }
          }
          // Claims challenge support: if API indicates insufficient_claims and provides claims, store them for login
          const challenge = parseWwwAuthenticate(wwwAuth);
          if (
            challenge &&
            (challenge.error === "insufficient_claims" || challenge.error === "invalid_token") &&
            challenge.claims
          ) {
            try {
              sessionStorage.setItem("MSAL_CLAIMS_CHALLENGE", challenge.claims);
            } catch {}
          }
          if (!suppressAuthRedirect && !isExactAuthError) {
            if (res.status === 401) {
              // Als de session-expired-modal gemount is: toon die i.p.v. hard te redirecten,
              // zodat de paginastate behouden blijft. Anders val terug op redirect.
              if (typeof window !== "undefined" && window.__sessionExpiredHandlerMounted) {
                window.dispatchEvent(
                  new CustomEvent("app:session-expired", { detail: { message: SESSIE_VERLOPEN } })
                );
              } else {
                redirectToLoginWithError(SESSIE_VERLOPEN);
              }
            } else {
              redirectToForbiddenWithError(GEEN_TOEGANG_PAGINA);
            }
          }
          throw new ApiError(message, {
            status: res.status,
            code: errorCode,
            correlationId: corr,
            responseBody: payload,
          });
        }
        const apiErr = new ApiError(message, {
          status: res.status,
          code: errorCode,
          correlationId: corr,
          responseBody: payload,
          retryAfterSeconds,
        });
        const canRetry = shouldRetry({
          method,
          status: res.status,
          isNetworkError: false,
          retryOnStatus,
          retryOnMethods,
        });
        if (canRetry && attempt < retries) {
          // 429 met Retry-After: de wachttijd van de server respecteren (max 30 s) i.p.v. jitter.
          const delay =
            res.status === 429 && retryAfterSeconds != null
              ? Math.min(retryAfterSeconds * 1000, MAX_RETRY_AFTER_MS)
              : withJitter(retryDelayBaseMs, attempt);
          console.warn("API retry", {
            url,
            method,
            status: res.status,
            attempt,
            nextDelayMs: delay,
            correlationId: corr,
          });
          await sleep(delay, signal);
          continue;
        }
        console.error("API error", {
          url,
          method,
          status: res.status,
          statusText: res.statusText,
          correlationId: corr,
          responseBody: payload,
        });
        lastError = apiErr;
        break;
      }

      return { data: payload, correlationId: corr, status: res.status };
    } catch (err) {
      clearTimeout(to);
      signal?.removeEventListener?.("abort", onExternalAbort);
      // Afgebroken door de caller: nooit retryen, en niet als timeout/netwerkfout melden.
      if (signal?.aborted) throw abortedError();
      // De 401/403-tak hierboven throwt binnen deze try. Zonder deze guard vangt
      // de catch zijn eigen ApiError en herschrijft die tot "Network error",
      // waardoor status/code/correlationId verloren gaan en een echt
      // serverantwoord niet meer te onderscheiden is van een netwerkfout.
      if (err instanceof ApiError && err.status != null) {
        lastError = err;
        break;
      }
      const isTimeout =
        err?.name === "AbortError" || err?.message === "timeout" || err === "timeout";
      const networkErr = new ApiError(
        isTimeout ? `Timeout after ${effectiveTimeout}ms` : "Network error",
        { isTimeout, isNetwork: !isTimeout }
      );
      // Na onze eigen timeout NIET opnieuw proberen: de server rekent dan meestal nog aan de eerste
      // aanvraag, een herhaling verdubbelt enkel de DB-last (#79). Echte netwerkfouten wel.
      const canRetry =
        !isTimeout &&
        shouldRetry({
          method,
          status: null,
          isNetworkError: true,
          retryOnStatus,
          retryOnMethods,
        });
      if (canRetry && attempt < retries) {
        const delay = withJitter(retryDelayBaseMs, attempt);
        console.warn("API retry (network)", { url, method, attempt, nextDelayMs: delay });
        await sleep(delay, signal);
        lastError = networkErr;
        continue;
      }
      lastError = networkErr;
      break;
    }
  }

  if (lastError) throw lastError;
  throw new ApiError("Unknown API error");
}
